/* The reading half of Detail: everything Gemini pulled out of one item.
 *
 * This column is the reason Detail is not just a player. v1 keeps the same
 * material behind a drag-up sheet, which is the right answer on a phone and the
 * wrong one on a desktop — it hides the extracted knowledge behind a gesture on
 * the one screen with room to show it (§30: a knowledge browser, not Instagram).
 *
 * Ordering is deliberate and is the answer to "the transcript is long". Places
 * and Index come first because they are the actionable, scannable parts — a
 * place chip is a jump to the map, and an Index entry is a thing you might go
 * buy. The transcript is the bulk and the least scannable, so it sits last and
 * arrives collapsed (§27, progressive disclosure).
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import type { DetailPlace, ItemDetail } from '../../core/types.js';
import { clean, fmtDuration } from '../../../../packages/core/index.js';
import { kindMeta } from '../g-map/kinds.js';
import '../../component/g-button/g-button.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';

/** Lines of transcript shown before "Show all". Three fills the eye without burying Index. */
const CLAMP_LINES = 3;

/**
 * A place is a jump target only if there is somewhere on the map to jump to.
 *
 * Three ways there isn't, and they are genuinely different: no coordinates (3
 * venues), no canonical venue at all (rows predating the Places backfill), or
 * the user deleted this venue from their map. The last one is invisible from
 * here without the server saying so — `/api/places` simply omits it, so the jump
 * would land on Map and select nothing.
 */
const isJumpable = (p: DetailPlace): boolean =>
  p.geocoded && p.map_place_id != null && p.on_map;

/** Why this place isn't clickable, for the tooltip. Empty when it is. */
function offMapReason(p: DetailPlace): string {
  if (isJumpable(p)) return '';
  if (!p.on_map) return 'You removed this from your map';
  return 'No location for this one';
}

@customElement('knowledge-panel')
export class KnowledgePanel extends LitElement {
  static override styles = [
    shadowReset,
    css`
      /* No top padding on the host: this element is the scroll port, and a
         sticky child's zero top offset pins to the *padding* edge, not the border
         edge. With padding here the heading would come to rest 24px down and
         content would scroll visibly through the band above it. The space is
         moved onto the heading, which owns it and carries it while stuck. */
      /* --content-* rather than --space-5: it resolves to exactly --space-5 on
         desktop and to the phone's own inset below the breakpoint, so one
         expression covers both and the safe-area inset comes along for free
         (ADR 0009 decision 3). */
      :host {
        display: block;
        padding: 0 var(--content-r) var(--space-5) var(--content-l);
        font-family: var(--font-ui);
      }
      /* Sticky: the column scrolls a long way (transcript, places, index) and
         losing which item you are reading about is the one thing it can't
         afford. The negative margins pull it out to the host's padding edge so
         text scrolls *under* it rather than past it through the side gutters. */
      h2 {
        position: sticky;
        top: 0;
        z-index: 2;
        margin-inline: calc(var(--content-l) * -1) calc(var(--content-r) * -1);
        margin-block: 0;
        padding: var(--space-5) var(--content-r) var(--space-3) var(--content-l);
        border-bottom: 1px solid var(--border);
        background: var(--bg);
        font-family: var(--font-display);
        font-size: 1.25rem;
        font-weight: 700;
        line-height: 1.25;
      }
      .byline {
        margin-top: var(--space-2);
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.72rem;
      }
      /* The serif is v2's reading face. The summary is the one piece of prose
         here that a person reads start to finish, so it gets it. */
      .summary {
        margin: var(--space-4) 0 0;
        font-family: var(--font-text);
        font-size: 1rem;
        line-height: 1.55;
      }
      section {
        margin-top: var(--space-5);
      }
      .label {
        margin-bottom: var(--space-2);
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.66rem;
        font-weight: 600;
        letter-spacing: 0.09em;
        text-transform: uppercase;
      }
      .chips {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
      }
      /* Same hue vocabulary as the pin, the map row and the kind chip — the
         whole point of the palette is that it survives crossing a screen. */
      .place {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: 4px var(--space-3) 4px var(--space-2);
        border: 1px solid color-mix(in oklab, var(--kc) 45%, transparent);
        border-radius: var(--radius-full);
        background: color-mix(in oklab, var(--kc) var(--k-tint), transparent);
        color: var(--text);
        font-family: var(--font-ui);
        font-size: 0.82rem;
        text-align: left;
        --icon-size: 14px;
      }
      button.place {
        cursor: pointer;
      }
      /* Guarded: on touch a hover latches and outlives the tap (see g-chips) —
         and a latched kind tint here is the same false signal as on a chip. */
      @media (hover: hover) {
        button.place:hover {
          border-color: var(--kc);
          background: color-mix(in oklab, var(--kc) 26%, transparent);
        }
      }
      /* One step past hover, in the chip's own hue rather than the neutral wash:
         --press over a kind-tinted chip greys the hue that ties it to its pin. */
      button.place:active {
        border-color: var(--kc);
        background: color-mix(in oklab, var(--kc) 40%, transparent);
      }
      .place g-icon {
        color: var(--kc);
      }
      /* Visited, in the same teal check Map's row uses — one appearance for one
         state, across two screens (§28). It trails the label instead of
         replacing the leading icon, because kind and visited are independent
         facts and the kind is the one you scan for.

         The class selector beats the .place g-icon rule on specificity, which
         is what keeps it out of the kind's hue. */
      .place .vis {
        color: var(--k-stay);
        --icon-size: 12px;
      }
      /* On a chip that is already dimmed for being off the map, the check dims
         with it — a bright accent inside a greyed-out chip reads as the live
         part of something inert. */
      .place.off .vis {
        color: inherit;
      }
      /* Not a jump target and saying so quietly: a venue we could not geocode
         has nowhere to send you, and a chip that looks clickable and isn't is
         worse than one that plainly isn't. */
      .place.off {
        border-style: dashed;
        color: var(--text-dim);
      }
      .mention {
        display: inline-flex;
        align-items: center;
        padding: 4px var(--space-3);
        border: 1px solid var(--border);
        border-radius: var(--radius-full);
        background: var(--surface);
        font-size: 0.82rem;
      }
      .said {
        margin: 0;
        font-family: var(--font-text);
        font-size: 0.95rem;
        line-height: 1.6;
        white-space: pre-wrap;
      }
      .said.clamped {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${CLAMP_LINES};
        line-clamp: ${CLAMP_LINES};
        overflow: hidden;
      }
      .more {
        margin-top: var(--space-2);
        padding: 0;
        border: 0;
        background: none;
        color: var(--accent);
        font-family: var(--font-ui);
        font-size: 0.8rem;
        font-weight: 600;
        cursor: pointer;
      }
      @media (hover: hover) {
        .more:hover {
          text-decoration: underline;
        }
      }
      /* Bare inline text with no box of its own — a background wash would paint a
         rectangle around a word. It dims instead. */
      .more:active {
        opacity: 0.6;
      }
      .foot {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-top: var(--space-6);
        padding-top: var(--space-4);
        border-top: 1px solid var(--border);
      }
      .facts {
        margin-top: var(--space-4);
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.7rem;
        line-height: 1.6;
      }
      .source {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        color: var(--text);
        font-size: 0.85rem;
        text-decoration: none;
        --icon-size: 14px;
      }
      @media (hover: hover) {
        .source:hover {
          background: var(--surface-2);
        }
      }
      .spacer {
        flex: 1;
      }
      /* The skeleton stands in for the heading, so it owns the top space the
         heading normally carries. */
      .skel {
        border-radius: var(--radius-sm);
        background: var(--surface-2);
      }
      .skel:first-child {
        margin-top: var(--space-5);
      }
      .skel + .skel {
        margin-top: var(--space-3);
      }

      /* ── Phone: touch sizes (ADR 0013) ───────────────────────────────
         These chips WRAP, so an extension sized against the WRONG axis would
         overlap the row above or below it — the sliver between two lines would
         activate whichever chip is later in DOM order, with nothing on screen
         to explain it (that trap is written up in g-chips.ts, gated there to a
         single-line row for the same reason). The fix isn't to skip the
         extension, it's to size it against the row gap instead of the other
         way around: a 6px vertical-only extension needs 12px of clearance
         between wrapped rows to meet without overlapping, and .chips' gap is
         already exactly var(--space-3) = 12px. Real height drops to 32px
         (g-chips' own 36px baseline minus the same 4px margin Material uses),
         reaching a 44px hit area (32 + 6 + 6) the same way g-chips reaches its
         44 from 36 + 4 + 4 — same arithmetic, re-derived for a row that wraps
         instead of scrolling. */
      @media (max-width: 959px) {
        /* The sheet's own head (drag handle + close button) sits directly
           above this heading, already reading as a boundary — a second
           hairline right under the title plus desktop's full padding stacks
           into one blocky double-band. Kept sticky (still the only place the
           title shows once the sheet covers the video's own title strip,
           see the "Sticky" comment above) but shed the redundant border and
           the extra padding that made it read as boxed-in.

           The background swap matters as much as the padding: on desktop
           this column sits directly on --bg, but on phone it's slotted into
           g-sheet's body, which paints itself --surface (a shade lighter).
           --bg here painted a visibly different-coloured strip behind the
           sticky title than the surface it's stuck to. */
        h2 {
          padding-block: var(--space-3) 0;
          border-bottom: none;
          background: var(--surface);
        }
        /* With the border gone, the old gaps (tuned to separate boxed title
           from the content below it) just read as slack between lines that
           belong together. Title, byline and summary tighten into one
           cluster; the bigger gap before Places (section's own --space-5)
           is what now carries the "new section" signal, unchanged. */
        .byline {
          margin-top: var(--space-1);
        }
        .summary {
          margin-top: var(--space-2);
        }
        .chips {
          gap: var(--space-3);
        }
        .place,
        .mention {
          position: relative;
          min-height: 32px;
          padding-inline: var(--space-3);
        }
        .place {
          padding-left: var(--space-2);
        }
        .place::after,
        .mention::after {
          content: '';
          position: absolute;
          inset: -6px 0;
        }
        /* Still an inline link, deliberately — WCAG 2.5.8 exempts targets in a
           sentence of text, and boxing this would give a minor disclosure
           control more weight than the transcript it opens. It only needed to
           stop being 15px tall. */
        .more {
          padding: var(--space-2) 0;
        }
        .foot {
          gap: var(--space-3);
        }
        .foot .source {
          min-height: 44px;
        }
        .foot g-button {
          --g-button-pad: 13px;
          --icon-size: 18px;
        }
      }
    `,
  ];

  @property({ attribute: false }) detail: ItemDetail | null = null;
  @property({ type: Boolean }) loading = false;

  @state() private expanded = false;

  override updated(changed: Map<string, unknown>): void {
    if (!changed.has('detail')) return;
    // A new item starts collapsed. Without this, expanding one transcript
    // expands every one you page to afterwards.
    this.expanded = false;
    // ...and at the top. This element is the scroll port and it is never torn
    // down between items, so the offset would otherwise carry over — and with
    // neighbours pre-fetched, `detail` goes straight from one item to the next
    // without ever passing through null, so nothing collapses the content and
    // clamps the scroll back for us.
    this.scrollTop = 0;
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
  }

  private renderPlaces(places: DetailPlace[]) {
    if (!places.length) return nothing;
    return html`
      <section>
        <div class="label">Places</div>
        <div class="chips">
          ${places.map((p) => {
            // A venue removed from the map keeps its kind's icon and hue — it is
            // still a Food place, we just aren't showing you the pin. Only a
            // venue with no coordinates takes the grey `ban` treatment, because
            // there "no location" is the whole fact about it.
            const meta = kindMeta(p.kind, p.geocoded);
            const style = `--kc: ${meta.color}`;
            const label = clean(p.name) || p.name;
            const visited = p.visited
              ? html`<g-icon class="vis" name="check" title="You've been here"></g-icon>`
              : nothing;
            if (!isJumpable(p)) {
              return html`<span class="place off" style=${style} title=${offMapReason(p)}>
                <g-icon name=${meta.icon}></g-icon>${label}${visited}
              </span>`;
            }
            return html`<button
              class="place"
              style=${style}
              title="Show on the map"
              @click=${() => this.emit('show-place', p.map_place_id)}
            >
              <g-icon name=${meta.icon}></g-icon>${label}${visited}
            </button>`;
          })}
        </div>
      </section>
    `;
  }

  private renderSaid(transcript: string) {
    const text = clean(transcript);
    if (!text) return nothing;
    // Clamping is a paint-time decision, so "is there more" can't be measured
    // without a layout read. Approximating by length is enough for a control
    // that only ever offers to show slightly more than is already there.
    const long = text.length > 220;
    return html`
      <section>
        <div class="label">Said</div>
        <p class="said ${long && !this.expanded ? 'clamped' : ''}">${text}</p>
        ${long
          ? html`<button class="more" @click=${() => (this.expanded = !this.expanded)}>
              ${this.expanded ? 'Show less' : 'Show all'}
            </button>`
          : nothing}
      </section>
    `;
  }

  override render() {
    const d = this.detail;
    if (!d) {
      return this.loading
        ? html`
            <div class="skel" style="height:26px;width:70%"></div>
            <div class="skel" style="height:14px;width:40%"></div>
            <div class="skel" style="height:76px"></div>
          `
        : nothing;
    }

    const title = clean(d.title) || 'Untitled';
    const summary = clean(d.summary ?? '');
    const byline = [
      d.creator ? `@${d.creator}` : '',
      d.duration_s ? fmtDuration(d.duration_s) : '',
      clean(d.category),
    ].filter(Boolean);
    const facts = [
      d.saved_at ? `saved ${d.saved_at.slice(0, 10)}` : '',
      d.posted_at ? `posted ${d.posted_at.slice(0, 10)}` : '',
      clean(d.collection ?? ''),
      clean(d.language ?? ''),
    ].filter(Boolean);

    return html`
      <h2>${title}</h2>
      ${byline.length ? html`<div class="byline">${byline.join(' · ')}</div>` : nothing}
      ${summary && summary !== title ? html`<p class="summary">${summary}</p>` : nothing}
      ${this.renderPlaces(d.places)}
      ${d.mentions.length
        ? html`<section>
            <div class="label">Index</div>
            <div class="chips">
              ${d.mentions.map(
                (m) => html`<span class="mention" title=${clean(m.detail)}
                  >${clean(m.name) || m.name}</span
                >`,
              )}
            </div>
          </section>`
        : nothing}
      ${this.renderSaid(d.transcript ?? '')}
      ${facts.length ? html`<div class="facts">${facts.join(' · ')}</div>` : nothing}

      <div class="foot">
        <a class="source" href=${d.url} target="_blank" rel="noopener">
          <g-icon name="external"></g-icon>Source
        </a>
        <span class="spacer"></span>
        <g-button
          variant="danger"
          icon-only
          label="Remove from library"
          @click=${() => this.emit('remove')}
        >
          <g-icon name="trash"></g-icon>
        </g-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'knowledge-panel': KnowledgePanel;
  }
}
